import { items } from '../../items';
import { Location } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { EventsService } from 'src/app/core/services/events.service';
import { SessionService } from 'src/app/core/services/session.service';
import { createIvrForm, resolveLineForwarding } from 'src/app/menus/services/customer-form.service';
import { MenuHelpService } from 'src/app/menus/services/menu-help.service';
import { IVR } from 'src/app/core/models/customer.model';
import { ToasterService } from 'src/app/shared/notification-toast/toaster.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { NumberGridService } from 'src/app/features/numbers/services/number-grid.service';
import { ImpersonationService } from 'src/app/core/services/impersonation.service';
import { Role } from 'src/app/core/enums/role.enum';

@Component({
  selector: 'app-portal-header',
  templateUrl: './portal-header.component.html',
  styleUrls: ['./portal-header.component.scss'],
})
export class PortalHeaderComponent implements OnInit {
  @ViewChild('anchor', { static: false })
  public anchor!: ElementRef<HTMLElement>;
  public margin = { horizontal: -46, vertical: 7 };
  public toggleText = 'Hide';
  showThemePopup = false;
  public pagetitle = '';
  // Mirrors the sidebar drawer's expanded/collapsed state so the logo can collapse with it.
  @Input() expanded = true;
  public ivrForm = createIvrForm();
  private forwardToggleSub?: Subscription;
  private unsubscribe = new Subject<void>();
  public switch = false;

  constructor(
    private location: Location,
    private events: EventsService,
    public session: SessionService,
    public help: MenuHelpService,
    private toastNotification: ToasterService,
    private router: Router,
    private numberService: NumberGridService,
    private impersonation: ImpersonationService
  ) {}

  // Change-number dialog state (super-admin only).
  public showChangeNumberDialog = false;
  public changeNumberValue = '';
  public changeNumberSaving = false;
  public readonly phoneMask = '(000) 000-0000';

  // The API authorizes off the REAL Auth0 token (RequireSuperAdmin resolves req.auth.sub, which
  // stays the admin's while impersonating), so keep the affordance visible in that case even
  // though session.user.roles have been swapped to the target's.
  public get canChangeNumber(): boolean {
    return (
      !!this.session.user?.selectedNumber?._id &&
      (!!this.session.user?.roles?.includes(Role.SuperAdmin) || this.impersonation.isImpersonating())
    );
  }

  public openChangeNumber($event: Event) {
    $event.preventDefault();
    // The pencil lives inside the badge button — don't also fire numberClick()'s /numbers navigate.
    $event.stopPropagation();
    this.changeNumberValue = (this.session.user?.selectedNumber?.number || '').replace(/^\+1/, '');
    this.showChangeNumberDialog = true;
  }

  public cancelChangeNumber() {
    this.showChangeNumberDialog = false;
  }

  public confirmChangeNumber() {
    const digits = (this.changeNumberValue || '').replace(/\D/g, '');
    if (digits.length !== 10) {
      this.toastNotification.error('Enter a valid 10-digit US phone number.', 'Ok');
      return;
    }

    this.changeNumberSaving = true;
    this.numberService
      .changeNumber(this.session.user!.selectedNumber!._id!, digits)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        // Full reload, same reason ImpersonationService.start() reloads: ExtensionsGridService and
        // the dashboard grid services capture session.user.selectedNumber.number in their
        // CONSTRUCTORS, so an in-place mutation would leave them filtering on the old number.
        next: () => window.location.reload(),
        error: (err) => {
          this.changeNumberSaving = false;
          this.toastNotification.error(err?.error?.message || 'Failed to change the number.', 'Ok');
        },
      });
  }

  ngOnInit(): void {
    // These pages render their own in-content title, so suppress the top page title there.
    const hideTopTitleFor = [
      '/demo',
      '/admin/users',
      '/dashboard',
      '/menus',
      '/account',
      '/availability',
      '/invoices',
      '/numbers',
      '/voicemails',
      '/extensions',
      '/speech',
    ];
    this.events.navigationClicked$.pipe(takeUntil(this.unsubscribe)).subscribe((x: any) => {
      this.pagetitle = hideTopTitleFor.includes(x.path) ? '' : x.text;
    });

    const item = items.find((obj) => obj.path?.toLowerCase() == this.location.path().toLowerCase());
    if (item) this.pagetitle = hideTopTitleFor.includes(item.path!) ? '' : item.text!;

    this.setDefaultNumber();
    this.buildIvrForm();

    // Forwarding is per line, so the switch must follow whichever number is being viewed.
    this.events.defaultNumberChanged$.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.buildIvrForm());
    // Saved from the Numbers page for the line shown here — reflect it unless mid-edit.
    this.events.forwardingChanged$.pipe(takeUntil(this.unsubscribe)).subscribe((numberId) => {
      if (numberId === this.session.user?.selectedNumber?._id && this.ivrForm.pristine) this.buildIvrForm();
    });
  }

  private get lineForwarding(): IVR {
    return resolveLineForwarding(this.session.user?.selectedNumber, this.session.user?.customer);
  }

  private buildIvrForm() {
    this.forwardToggleSub?.unsubscribe();
    this.ivrForm = createIvrForm(this.lineForwarding);
    this.forwardToggleSub = this.ivrForm.controls.forward?.valueChanges.subscribe(() => {
      this.ivrForm.controls.forwardNumber?.setValue(this.lineForwarding.forwardNumber ?? null);
    });
  }

  private setDefaultNumber() {
    if (this.session.user?.customer?.numbers && this.session.user?.customer?.numbers.length > 0) {
      let defaultNumber = this.session.user.customer.numbers.find((number) => number.isDefault);      
      if(defaultNumber){
        this.session.user.selectedNumber = defaultNumber;
      }
      else {
        this.numberService
          .updateDefaultNumber({
            _id: this.session.user.customer.numbers[0]._id,
            customer: this.session.user.customer._id,
          })
          .pipe(takeUntil(this.unsubscribe))
          .subscribe();
      }
    }
  }

  public ngOnDestroy(): void {
    this.forwardToggleSub?.unsubscribe();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  UpdateIvrStatus() {
    const number = this.session.user?.selectedNumber;
    if (!number?._id) {
      this.toastNotification.error('Select a phone number first.', 'Ok');
      return;
    }

    const ivrStatus = this.ivrForm.getRawValue() as IVR;
    // The switch reads "Enabled" when the IVR answers, i.e. the inverse of `forward`.
    const forward = !ivrStatus.forward;
    const forwardNumber = ivrStatus.forwardNumber || null;
    if (forward && !forwardNumber) {
      this.toastNotification.error('Enter a number to forward calls to.', 'Ok');
      return;
    }

    this.numberService
      .updateIvrStatus(number._id, forward, forwardNumber)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.ivrForm.markAsPristine();
          this.toastNotification.success(`IVR Status has been updated for ${number.name || number.number}!`, 'Ok');
        },
        error: (err) => this.toastNotification.error(err?.error?.message || 'Failed to update the IVR status.', 'Ok'),
      });
  }

  public onThemeToggle(): void {
    this.showThemePopup = !this.showThemePopup;
    this.toggleText = this.showThemePopup ? 'Hidе' : 'Show';
  }

  changeTheme(themeName: any) {
    const elem = document.getElementById('theme');
    if (elem) {
      elem.setAttribute('href', `assets/css/${themeName}.css`);
    }
    const body = document.getElementsByTagName('body')[0];
    if (body) {
      body.classList.toggle('theme-dark', !body.classList.contains('theme-dark'));
    }
    this.showThemePopup = false;
  }

  get isDarkMode() {
    const body = document.getElementsByTagName('body')[0];
    return body.classList.contains('theme-dark');
  }

  public activateClass(themeButtons: { active: boolean }) {
    themeButtons.active = !themeButtons.active;
  }

  public numberClick($event: Event) {
    $event.preventDefault();
    this.router.navigate(['/numbers']);
  }

  copyAndDial(event: MouseEvent): void {
    // Prevent the default link action
    event.preventDefault();
    const numberToCopy = this.session.user!.selectedNumber?.number;
    // Copy the number to the clipboard
    if (numberToCopy) {
      navigator.clipboard.writeText(numberToCopy).catch(err => {
        console.error('Failed to copy number: ', err);
      });
    }
  }  
}
